import React, { useState, useEffect, useCallback } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiBaseUrl } from '../config/runtime';

interface Order {
  id: number;
  service_id: number;
  service_title: string;
  thumbnail_image?: string;
  client_nickname?: string;
  freelancer_nickname?: string;
  package_type: 'basic' | 'standard' | 'premium';
  price: number;
  delivery_days: number;
  revisions: number;
  requirements?: string;
  status: string;
  payment_status: string;
  created_at: string;
  started_at?: string;
  delivered_at?: string;
  completed_at?: string;
}

const OrdersPage: React.FC = () => {
  const { isClientMode } = useMode();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('common.loginRequired'));
        return;
      }

      const endpoint = isClientMode 
        ? apiBaseUrl('/orders/my-orders')
        : apiBaseUrl('/orders/received');

      let url = `${endpoint}?page=${page}&limit=10`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setOrders(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
      } else {
        setError(result.message || t('orders.loadFailed'));
      }
    } catch (err) {
      console.error('주문 목록 조회 오류:', err);
      setError(t('orders.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, isClientMode, t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(apiBaseUrl(`/orders/${orderId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const result = await response.json();

      if (result.success) {
        alert(t('orders.statusChanged'));
        fetchOrders();
      } else {
        alert(result.message || t('orders.changeFailed'));
      }
    } catch (err) {
      console.error('상태 변경 오류:', err);
      alert(t('orders.changeError'));
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: {[key: string]: string} = {
      pending: t('orders.statusPending'),
      accepted: t('chat.accepted'),
      in_progress: t('projects.statusInProgress'),
      delivered: t('orders.statusDelivered'),
      revision: t('orders.statusRevision'),
      completed: t('transactions.statusCompleted'),
      cancelled: t('transactions.statusCancelled'),
      disputed: t('transactions.statusDisputed')
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: {[key: string]: string} = {
      pending: '#ffc107',
      accepted: '#17a2b8',
      in_progress: '#007bff',
      delivered: '#6f42c1',
      revision: '#fd7e14',
      completed: '#28a745',
      cancelled: '#6c757d',
      disputed: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const getPackageLabel = (type: string) => {
    const labels: {[key: string]: string} = {
      basic: t('serviceDetail.pkgBasic'),
      standard: t('serviceDetail.pkgStandard'),
      premium: t('serviceDetail.pkgPremium')
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <h1 style={{ marginBottom: '10px' }}>
        {isClientMode ? t('orders.myOrders') : t('orders.receivedOrders')}
      </h1>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        {isClientMode ? t('orders.subtitleClient') : t('orders.subtitleFreelancer')}
      </p>

      {/* 필터 */}
      <div style={{ marginBottom: '20px' }}>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            fontSize: '14px'
          }}
        >
          <option value="">{t('orders.allStatus')}</option>
          <option value="pending">{t('orders.statusPending')}</option>
          <option value="accepted">{t('chat.accepted')}</option>
          <option value="in_progress">{t('projects.statusInProgress')}</option>
          <option value="delivered">{t('orders.statusDelivered')}</option>
          <option value="completed">{t('transactions.statusCompleted')}</option>
          <option value="cancelled">{t('transactions.statusCancelled')}</option>
        </select>
      </div>

      {/* 주문 목록 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
          {t('common.loading')}
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#dc3545' }}>
          {error}
        </div>
      ) : orders.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '50px', 
          background: '#f8f9fa',
          borderRadius: '12px',
          color: '#666'
        }}>
          {isClientMode ? t('orders.noOrdersClient') : t('orders.noOrdersFreelancer')}
          {isClientMode && (
            <div style={{ marginTop: '20px' }}>
              <button
                onClick={() => navigate('/services')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {t('orders.browseServices')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {orders.map(order => (
            <div
              key={order.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e9ecef',
                display: 'flex',
                gap: '20px',
                alignItems: 'flex-start'
              }}
            >
              {/* 썸네일 */}
              <div style={{
                width: '120px',
                height: '80px',
                borderRadius: '8px',
                backgroundColor: '#f0f0f0',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                {order.thumbnail_image ? (
                  <img 
                    src={order.thumbnail_image} 
                    alt={order.service_title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#adb5bd',
                    fontSize: '24px'
                  }}>
                    📦
                  </div>
                )}
              </div>

              {/* 주문 정보 */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>
                      {order.service_title}
                    </h3>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      {isClientMode 
                        ? t('transactions.expertName', { name: order.freelancer_nickname })
                        : t('transactions.clientName', { name: order.client_nickname })
                      }
                    </p>
                  </div>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: getStatusColor(order.status)
                  }}>
                    {getStatusLabel(order.status)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '20px', fontSize: '14px', color: '#495057', marginBottom: '15px' }}>
                  <span>{t('orders.package')}: {getPackageLabel(order.package_type)}</span>
                  <span>{t('quoteModal.price')}: {t('projects.budgetExact', { v: order.price.toLocaleString() })}</span>
                  <span>{t('quoteModal.days')}: {t('projectDetail.daysN', { n: order.delivery_days })}</span>
                  <span>{t('orders.orderDate')}: {formatDate(order.created_at)}</span>
                </div>

                {/* 액션 버튼 */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => navigate(`/orders/${order.id}`)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f8f9fa',
                      color: '#495057',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {t('orders.viewDetail')}
                  </button>

                  {/* 전문가용 버튼 */}
                  {!isClientMode && order.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'accepted')}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        {t('common.accept')}
                      </button>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'cancelled')}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        {t('common.reject')}
                      </button>
                    </>
                  )}

                  {!isClientMode && order.status === 'accepted' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'in_progress')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {t('orders.startWork')}
                    </button>
                  )}

                  {!isClientMode && order.status === 'in_progress' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'delivered')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#6f42c1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {t('orders.deliver')}
                    </button>
                  )}

                  {/* 의뢰자용 버튼 */}
                  {isClientMode && order.status === 'delivered' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'completed')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {t('orders.confirmComplete')}
                    </button>
                  )}

                  {isClientMode && order.status === 'pending' && order.payment_status === 'pending' && (
                    <button
                      onClick={() => navigate(`/payment?orderId=${order.id}&amount=${order.price}&orderName=${encodeURIComponent(order.service_title)}`)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {t('quoteMsg.pay')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '10px', 
          marginTop: '30px' 
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              backgroundColor: page === 1 ? '#f5f5f5' : 'white',
              cursor: page === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            {t('common.prev')}
          </button>
          <span style={{ padding: '10px 20px' }}>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              backgroundColor: page === totalPages ? '#f5f5f5' : 'white',
              cursor: page === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
